/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Input, Modal, Tabs, Typography, Col, Form } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import MediaUpload from '../UI/MediaUpload';
import MediaGridTable from '../Tables/MediaGridTable';

// Misc import
import { useDeleteMediaFileMutation, useFetchMediaFilesQuery } from '../../store/media/mediaApi';
import { isValidImageURL } from '../../utils/string/isValidImageURL';
import { transformFileSize } from '../../utils/misc/transformFileSize';
import { MAX_UPLOAD_FILE_SIZE } from '../../constants/main';
import useDebouncedCallback from '../../utils/hooks/useDebounce';

// Constant declarations
const { TabPane } = Tabs;
const TABS = {
  UPLOAD: 'upload',
  LIBRARY: 'library',
  URL: 'url'
};
const pageSize = 16;

const MediaWidgetModal = ({ onClose = () => {}, isModalOpen, hasActiveTab, isPublicToOrg = true, onSuccess = () => {}, title }) => {
  const [activeTab, setActiveTab] = useState(TABS.UPLOAD);
  const { currentUser } = useSelector((state) => state.auth);
  const [form] = Form.useForm();

  const { t } = useTranslation();
  const { orgId } = useParams();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [, updateState] = useState();

  const urlParamsData = {
    view: 'grid',
    page: page,
    pageSize: pageSize,
    sortBy: 'createdAt',
    order: 'DESC',
    ...(searchTerm && { search: searchTerm })
  };
  if ( !isPublicToOrg ) {
    urlParamsData.createdByUserId = currentUser.id;
  }

  // eslint-disable-next-line no-unused-vars
  const { data: _ } = useFetchMediaFilesQuery({
    orgId,
    urlParams: urlParamsData
  }, { skip: activeTab !== TABS.LIBRARY });

  const [deleteMediaFile] = useDeleteMediaFileMutation({ fixedCacheKey: 'mediaFiles-delete' });

  useEffect(() => {
    if (!isModalOpen) {
      setActiveTab(TABS.UPLOAD);
      try {
        form.resetFields(['url', 'mediaFilesSearch']);
      } catch {
        /* Form may be unmounted */
      }
    }
  }, [isModalOpen]);

  const updateUrlSearchParams = async (current = 1) => {
    setPage(current);
    const search = form.getFieldValue('search');
    setSearchTerm(search);
  };

  const handleDelete = (data) => {
    deleteMediaFile({ orgId, id: data.id });
  };

  const insertImageUrl = async () => {
    const imageUrl = form.getFieldValue('url');
    if (imageUrl) {
      onSuccess(imageUrl);
    }
  };

  const debouncedOnChange = useDebouncedCallback(updateUrlSearchParams);

  return (
    <Modal
      title={typeof title === 'string' ? t(title) : t('Select / Upload Media File')}
      open={isModalOpen}
      onCancel={() => onClose(null)}
      style={{ maxWidth: 600 }}
      width={'95%'}
      centered
      footer={null}
    >
      <Typography.Text className="text-muted">
        {t('Upload media file or select one from Media Library. Maximum upload size file:')} {transformFileSize(MAX_UPLOAD_FILE_SIZE)}
      </Typography.Text>

      <Tabs
        activeKey={activeTab}
        onTabClick={(key) => setActiveTab(key)}
        destroyInactiveTabPane
        className={'mt-4'}
      >
        <TabPane
          key={TABS.UPLOAD}
          tab={t('Upload Files')}
          className="mb-5"
        >
          <MediaUpload hasCloseButton={false} isUploadVisible={true} onUploadComplete={onSuccess} isPublicToOrg={isPublicToOrg} showInfoText={false}/>
        </TabPane>

        <TabPane
          key={TABS.LIBRARY}
          tab={t('Media Library')}
          className="mb-4"
        >
          <Fragment>
            <Col>
              <Form
                name="mediaFilesSearch"
                initialValues={{
                  searchTerm: ''
                }}
                form={form}
              >
                <Form.Item
                  name="search"
                  className="w-50"
                >
                  <Input
                    size="large"
                    placeholder={t('Search in contents')}
                    allowClear
                    prefix={<SearchOutlined className="text-muted" />}
                    onChange={() => debouncedOnChange()}
                  />
                </Form.Item>
              </Form>
            </Col>
            <MediaGridTable page={page} pageSize={16} gridSize={6} imageHeight={125} handleDelete={handleDelete} urlSearchParams={updateUrlSearchParams} onClose={onClose}/>
          </Fragment>
        </TabPane>

        <TabPane
          key={TABS.URL}
          tab={t('Connect with URL')}
          className="mb-4"
        >
          <Col span={24} className="mt-2 mb-6">
            <Form
              form={form}
              layout="vertical"
              onFinish={insertImageUrl}
              onValuesChange={(_, allFields) => {
                updateState(allFields);
              }}
            >
              <Form.Item
                label={t('Enter Image URL')}
                name="url"
                validateTrigger={['onBlur']}
                rules={[
                  {
                    validator: async (_rule, value) => {
                      if (value) {
                        const isValid = await isValidImageURL(value);
                        if (!isValid) {
                          return Promise.reject(new Error(t('Please add a valid image URL')));
                        }
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input placeholder={t('Enter URL')} size="large" />
              </Form.Item>

              <Button size="large" type="primary" htmlType="submit" className={'float-right'}>
                {t('Add image')}
              </Button>
            </Form>
          </Col>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

MediaWidgetModal.propTypes = {
  onClose: PropTypes.func,
  isModalOpen: PropTypes.bool,
  hasActiveTab: PropTypes.bool,
  onSuccess: PropTypes.func,
  isPublicToOrg: PropTypes.bool,
  title: PropTypes.string
};

export default MediaWidgetModal;
